import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { CircleUser, Sparkles } from "lucide-react";

import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";

export default function SignInModal(props) {
  const {
    trigger,
    onSignIn,
    onForgotPassword,
    onCreateAccount,
    onMagicLink, // <-- add this
    showSocial = false,
  } = props || {};

  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [magicLoading, setMagicLoading] = React.useState(false);

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      if (onSignIn) await onSignIn({ email, password });
      setOpen(false);
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(err?.message || "We couldn’t sign you in. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink() {
    setError(null);

    if (!email) {
      setError("Please enter your email address first.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setMagicLoading(true);
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-member-magic-link`;

const res = await fetch(url, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ email }),
});

if (!res.ok) throw new Error("Request failed");

// Always show neutral success
setError(null);
// optionally show a success state somewhere:
// "If an account exists for this email, we sent you a link."

      // keep modal open so they can read any “sent” toast you show elsewhere
      // (or close it if you prefer)
      // setOpen(false);
    } catch (err) {
      setError(
        err?.message || "We couldn’t send the magic link. Please try again."
      );
    } finally {
      setMagicLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" className="px-3">
            <span className="tracking-wide">Sign in</span>
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-prose mx-auto rounded-3xl sm:rounded-3xl bg-white/60 border-2 border-white">
        <div className="max-w-prose p-12 items-center">
          <DialogHeader>
            <DialogTitle className="font-kalice font-medium text-3xl mb-2 flex items-center">
            <CircleUser className="h-6 w-6 mr-2" /> Sign in
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-8 grid gap-6">
            <div className="grid gap-2 font-sans text-sm font-medium">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 text-black border-black bg-white focus:outline-none focus:ring-0"
                placeholder="you@example.com"
              />
            </div>

            <div className="grid gap-2 font-sans text-sm font-medium">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 text-black border-black bg-white focus:outline-none focus:ring-0"
                placeholder="••••••••"
              />
            </div>

            {error && <p className="text-sm text-red-600 -mt-2">{error}</p>}

            <div className="flex items-center justify-between text-sm font-sans">
              <Button
                type="button"
                variant="link"
                onClick={() => onForgotPassword?.(email)}
                className="hover:no-underline hover:bg-white bg-transparent hover:bg-transparent hover:text-black text-gray-400 p-0 h-auto"
              >
                Forgot password?
              </Button>

              <Button
                asChild
                variant="link"
                className="hover:no-underline hover:bg-white bg-transparent hover:bg-transparent hover:text-black text-gray-400 p-0 h-auto"
              >
                <a href="/requestaccess">Become a member</a>
              </Button>
            </div>

            <DialogFooter className="mt-2">
              <Button
                type="submit"
                className="rounded-none w-full py-6"
                disabled={loading}
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </DialogFooter>

            {/* OR divider + Magic Link (same styling as your page version) */}
            <div className="flex items-center gap-4 py-0">
              <div className="h-px w-full bg-gray-400" />
              <span className="text-xs font-sans text-gray-400 tracking-widest">
                OR
              </span>
              <div className="h-px w-full bg-gray-400" />
            </div>

            <Button
              type="button"
              onClick={handleMagicLink}
              disabled={magicLoading}
              className="rounded-none w-full py-6"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {magicLoading ? "Sending magic link…" : "Login with Magic Link"}
            </Button>
          </form>

          {showSocial && (
            <>
              <div className="py-6">
                <Separator />
              </div>
              <div className="grid gap-3">
                <Button
                  variant="outline"
                  className="rounded-none w-full"
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("signin:google"))
                  }
                >
                  <FcGoogle className="mr-2 h-4 w-4" />
                  Continue with Google
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none w-full"
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("signin:apple"))
                  }
                >
                  <FaApple className="mr-2 h-4 w-4" />
                  Continue with Apple
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}



